'use client';

import { useState, useCallback, useMemo } from 'react';
import { LocaleLink } from '@/core/i18n';
import { ChevronDown, ArrowRight, ShieldCheck, BookOpen, Lock } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { usePageContent } from '@/core/i18n/content';
import { TEMPLATE_CHROME } from '@/core/i18n/content/template-chrome.content';
import type { TemplateChromeContent } from '@/core/i18n/content/template-chrome.types';
import { COMPLIANCE_REGION_TRANSLATIONS } from '@/core/i18n/content/compliance-region.content';
import type { ComplianceRegionText } from '@/core/i18n/content/compliance-region.content';
import { useLocaleStore } from '@/core/i18n/locale.store';
import type { RegionalComplianceConfig } from './seo-compliance.data';
import { COMPLIANCE_CONFIGS } from './seo-compliance-extended.data';

const pfx = 'compliance';

/* -------------------------------------------------------------------------- */
/*  Section — Hero                                                            */
/* -------------------------------------------------------------------------- */

function ComplianceHero({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-hero`} className="relative w-full overflow-hidden bg-hms-bg">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 30%, rgba(200,145,58,0.08) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-4xl px-4 pb-16 pt-24 text-center sm:px-6 sm:pt-32 lg:px-8 lg:pt-40">
        <div className="mb-4 flex items-center justify-center gap-2 text-sm text-hms-textSub">
          <ShieldCheck className="h-4 w-4 text-hms-gold" />
          <span>{t.common.compliance} — {text?.region ?? config.region}</span>
        </div>
        <h1
          id={`${pfx}-title`}
          className={cn(
            'font-poppins text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {text?.title ?? config.title}
        </h1>
        <p id={`${pfx}-subtitle`} className="mx-auto mt-6 max-w-2xl text-base text-hms-textSub sm:text-lg">
          {text?.subtitle ?? config.subtitle}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Compliance Features Grid                                        */
/* -------------------------------------------------------------------------- */

function ComplianceFeatures({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-features`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-features-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.complianceChrome.featuresFor.replace('{0}', text?.region ?? config.region)}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-hms-textSub">
          {t.complianceChrome.everyRegulation}
        </p>
        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {config.complianceFeatures.map((feat, idx) => {
            const Icon = feat.icon;
            const tf = text?.features[idx];
            return (
              <div
                key={idx}
                id={`${pfx}-feature-${idx}`}
                className={cn(
                  'group rounded-xl border border-hms-border bg-hms-card p-6',
                  'transition-all duration-300 hover:border-hms-gold/20 hover:-translate-y-1 hover:shadow-lg hover:shadow-black/20',
                )}
              >
                <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-hms-gold/10">
                  <Icon className="h-5 w-5 text-hms-gold" />
                </div>
                <h3 className="mb-2 text-base font-bold tracking-tight text-hms-text">{tf?.title ?? feat.title}</h3>
                <p className="text-sm leading-relaxed text-hms-textSub">{tf?.description ?? feat.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Tax Details Table                                               */
/* -------------------------------------------------------------------------- */

function ComplianceTaxTable({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-tax-table`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-tax-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.complianceChrome.taxLevyDetails}
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-hms-textSub">
          {t.complianceChrome.allTaxesCalculated}
        </p>
        <div className="mt-12 overflow-hidden overflow-x-auto rounded-xl border border-hms-border">
          <table id={`${pfx}-tax-data`} className="w-full">
            <thead>
              <tr className="border-b border-hms-border bg-hms-card">
                <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-hms-textSub">
                  {t.complianceChrome.taxLevy}
                </th>
                <th className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-hms-textSub">
                  {t.complianceChrome.rate}
                </th>
                <th className="hidden px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-hms-textSub sm:table-cell">
                  {t.complianceChrome.description}
                </th>
              </tr>
            </thead>
            <tbody>
              {config.taxDetails.map((tax, idx) => {
                const tt = text?.taxDetails[idx];
                return (
                  <tr
                    key={idx}
                    id={`${pfx}-tax-row-${idx}`}
                    className={cn(
                      'border-b border-hms-border/50 transition-colors hover:bg-hms-card/50',
                      idx % 2 === 0 ? 'bg-hms-bg' : 'bg-hms-surface/30',
                    )}
                  >
                    <td className="px-5 py-4 text-sm font-medium text-hms-text">{tt?.name ?? tax.name}</td>
                    <td className="px-5 py-4 text-sm font-semibold text-hms-gold">{tt?.rate ?? tax.rate}</td>
                    <td className="hidden px-5 py-4 text-sm text-hms-textSub sm:table-cell">{tt?.description ?? tax.description}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Guest Registration                                              */
/* -------------------------------------------------------------------------- */

function ComplianceGuestRegistration({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-guest-reg`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-hms-gold/10">
            <BookOpen className="h-6 w-6 text-hms-gold" />
          </div>
        </div>
        <h2
          id={`${pfx}-guest-reg-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.complianceChrome.guestRegistration}
        </h2>
        <div
          id={`${pfx}-guest-reg-content`}
          className="mt-8 rounded-xl border border-hms-border bg-hms-card p-6 sm:p-8"
        >
          <p className="text-sm leading-relaxed text-hms-textSub">{text?.guestRegistration ?? config.guestRegistration}</p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — Data Protection                                                 */
/* -------------------------------------------------------------------------- */

function ComplianceDataProtection({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-data-prot`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-hms-teal/10">
            <Lock className="h-6 w-6 text-hms-teal" />
          </div>
        </div>
        <h2
          id={`${pfx}-data-prot-title`}
          className="text-center text-2xl font-bold tracking-tight text-hms-text sm:text-3xl"
        >
          {t.complianceChrome.dataProtection}
        </h2>
        <div
          id={`${pfx}-data-prot-content`}
          className="mt-8 rounded-xl border border-hms-border bg-hms-card p-6 sm:p-8"
        >
          <p className="text-sm leading-relaxed text-hms-textSub">{text?.dataProtection ?? config.dataProtection}</p>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — FAQ Accordion                                                   */
/* -------------------------------------------------------------------------- */

function ComplianceFAQ({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = useCallback((i: number) => setOpenIndex((p) => (p === i ? null : i)), []);

  return (
    <section id={`${pfx}-faq`} className="relative w-full overflow-hidden bg-hms-surface py-20 sm:py-28">
      <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-faq-title`}
          className={cn(
            'text-center text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.common.faq}
        </h2>
        <div className="mt-12 space-y-3">
          {config.faqItems.map((item, idx) => {
            const fq = text?.faqItems[idx];
            return (
            <div
              key={idx}
              id={`${pfx}-faq-item-${idx}`}
              className={cn(
                'rounded-xl border transition-colors',
                openIndex === idx
                  ? 'border-hms-gold/30 bg-hms-card'
                  : 'border-hms-border bg-hms-card/50 hover:border-hms-gold/15',
              )}
            >
              <button
                id={`${pfx}-faq-toggle-${idx}`}
                type="button"
                onClick={() => toggle(idx)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <span
                  className={cn(
                    'text-sm font-semibold leading-snug',
                    openIndex === idx ? 'text-hms-gold' : 'text-hms-text',
                  )}
                >
                  {fq?.question ?? item.question}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 transition-transform duration-200',
                    openIndex === idx ? 'rotate-180 text-hms-gold' : 'text-hms-textDim',
                  )}
                />
              </button>
              {openIndex === idx && (
                <div className="px-5 pb-5">
                  <p className="text-sm leading-relaxed text-hms-textSub">{fq?.answer ?? item.answer}</p>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Section — CTA                                                             */
/* -------------------------------------------------------------------------- */

function ComplianceCTA({ config, t, text }: { config: RegionalComplianceConfig; t: TemplateChromeContent; text: ComplianceRegionText | null }) {
  return (
    <section id={`${pfx}-cta`} className="relative w-full overflow-hidden bg-hms-bg py-20 sm:py-28">
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 50%, rgba(200,145,58,0.06) 0%, transparent 70%)' }}
      />
      <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
        <h2
          id={`${pfx}-cta-title`}
          className={cn(
            'text-3xl font-extrabold tracking-tight sm:text-4xl',
            'bg-gradient-to-r from-hms-gold via-hms-goldLt to-amber-400 bg-clip-text text-transparent',
          )}
        >
          {t.complianceChrome.stayCompliant.replace('{0}', text?.region ?? config.region)}
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-hms-textSub sm:text-lg">
          {t.complianceChrome.handlesEveryTax}
        </p>
        <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
          <LocaleLink
            id={`${pfx}-cta-final-trial`}
            href="/register-hotel"
            className={cn(
              'inline-flex items-center gap-2 rounded-lg bg-hms-gold px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-bg shadow-lg shadow-hms-gold/20',
              'transition-all duration-200 hover:bg-hms-goldLt hover:shadow-hms-gold/30 active:scale-[0.98]',
              'w-full sm:w-auto justify-center',
            )}
          >
            {t.cta.trialBtn}
            <ArrowRight className="h-4 w-4" />
          </LocaleLink>
          <LocaleLink
            id={`${pfx}-cta-final-contact`}
            href="/contact"
            className={cn(
              'inline-flex items-center justify-center rounded-lg border border-hms-border px-8 py-3.5',
              'text-sm font-semibold tracking-wide text-hms-text',
              'transition-all duration-200 hover:border-hms-gold/40 hover:text-hms-gold',
              'w-full sm:w-auto',
            )}
          >
            {t.cta.contactBtn}
          </LocaleLink>
        </div>
        <p className="mt-5 text-xs text-hms-textDim">
          {t.common.noCreditCard}
        </p>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Main Component                                                            */
/* -------------------------------------------------------------------------- */

export function ComplianceLanding({ config, slug }: { config?: RegionalComplianceConfig; slug?: string }) {
  const t = usePageContent(TEMPLATE_CHROME);
  const storeLocale = useLocaleStore((s) => s.locale);
  const resolvedConfig = config ?? (slug ? COMPLIANCE_CONFIGS[slug] : undefined);
  const resolvedSlug = slug ?? config?.slug;
  const text = useMemo(() => {
    const locale = useLocaleStore.getState().locale;
    if (locale === 'en' || !resolvedSlug) return null;
    return COMPLIANCE_REGION_TRANSLATIONS[locale]?.[resolvedSlug] ?? null;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- storeLocale is the reactive trigger for locale changes
  }, [storeLocale, resolvedSlug]);
  if (!resolvedConfig) return null;

  return (
    <main id={`${pfx}-${resolvedConfig.slug}-page`}>
      <ComplianceHero config={resolvedConfig} t={t} text={text} />
      <ComplianceFeatures config={resolvedConfig} t={t} text={text} />
      <ComplianceTaxTable config={resolvedConfig} t={t} text={text} />
      <ComplianceGuestRegistration config={resolvedConfig} t={t} text={text} />
      <ComplianceDataProtection config={resolvedConfig} t={t} text={text} />
      <ComplianceFAQ config={resolvedConfig} t={t} text={text} />
      <ComplianceCTA config={resolvedConfig} t={t} text={text} />
    </main>
  );
}
